<script lang="ts" setup>
import { IconChevronRight, IconCircle, IconCircleCheckFilled, IconCircleDotFilled } from '@/icons';
interface Props {
  items: string[];
  activeIndex: number;
  completedIndexes?: number[];
  clickable?: boolean;
}
const {
  items = [],
  activeIndex = 0,
  completedIndexes = [],
  clickable = false,
} = defineProps<Props>();
const emit = defineEmits<{
  (e: 'step-click', index: number): void;
}>();

function isCompleted(index: number) {
  return completedIndexes.length ? completedIndexes.includes(index) : index < activeIndex;
}

function isClickable(index: number) {
  return clickable && (isCompleted(index) || index === activeIndex);
}

function onStepClick(index: number) {
  if (!isClickable(index)) {
    return;
  }
  emit('step-click', index);
}
</script>

<template>
  <div data-ui-component="UiStepper" :class="$style.root">
    <div v-for="(item, i) in items" :key="i" :class="$style.step">
      <component
        :is="isClickable(i) ? 'button' : 'div'"
        :type="isClickable(i) ? 'button' : undefined"
        data-testid="ui-stepper-step"
        :data-active="activeIndex === i"
        :data-completed="isCompleted(i)"
        :data-clickable="isClickable(i)"
        :class="[
          $style.stepContent,
          {
            [$style.completed]: isCompleted(i),
            [$style.active]: activeIndex === i,
            [$style.clickable]: isClickable(i),
          },
        ]"
        @click="onStepClick(i)"
      >
        <div :class="$style.itemIcon">
          <IconCircleCheckFilled v-if="isCompleted(i)" aria-hidden="true" color="#18a582" />
          <template v-else-if="i === activeIndex">
            <IconCircleDotFilled aria-hidden="true" color="black" />
          </template>
          <template v-else>
            <IconCircle aria-hidden="true" color="#D2D3D9" />
          </template>
        </div>
        <div :class="$style.stepLabel">
          {{ item }}
        </div>
      </component>
      <div v-if="i < items.length - 1" :class="$style.connector">
        <slot name="connector">
          <IconChevronRight aria-hidden="true" color="#D2D3D9" size="16" />
        </slot>
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
@use '../../styles/shared';
@use '../../styles/colors';

.root {
  display: inline-flex;
}

.step {
  display: flex;
  position: relative;
  flex-basis: 0;
  flex-grow: 1;
  max-width: 100%;
  text-align: center;
  justify-content: center;
  user-select: none;
}

.stepLabel {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-decoration: none;
  font-size: 13px;
  font-weight: 500;
}

.stepContent {
  all: unset;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  max-width: 260px;
  min-width: 80px;
  overflow: hidden;
  padding: 10px;
  border-radius: 10px;
  cursor: default;

  &:not(.active) {
    color: #978f91;
  }

  &.completed {
    color: #18a582;
  }

  &.clickable {
    cursor: pointer;

    &:hover,
    &:focus-visible {
      background: var(--ui2-shade-black-4, rgba(0, 0, 0, 0.04));
    }
  }
}

.connector {
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0px 4px;
}

.itemIcon {
  display: flex;
  flex-shrink: 0;
  position: relative;
  gap: 8px;
}
</style>
